import {
  BufferType, LogLevel, MediaType, Player, PlayerAPI, PlayerConfig, PlayerEvent, SourceConfig, SubtitleTrack,
} from 'bitmovin-player';
import { UIFactory } from 'bitmovin-player-ui';
import { MKPlayerConfiguration } from './MKPlayerConfiguration';
import { MKStream } from './MKStream';
import { MKEvent } from './events/MKEvent';
import { BufferLevel } from './BufferLevel';
import { MKPlayerEventCallback } from './events/MKPlayerEventCallback';
import { Logger } from './Logger';
import { HttpRequest, HttpRequestType } from 'bitmovin-player/types/core/NetworkAPI';
import { UrlType } from './events/UrlType';
import { URLHelper } from './URLHelper';

/**
 * MKPlayer is an HTML5 video player used for playback of HLS and DASH adaptive bitrate streams
 */
export class MKPlayer {
  private player: PlayerAPI;
  private stream: MKStream;
  /**
   * constructor used to create a new MKPlayer instance
   *
   * @param containerElement - container that the video player will be rendered in
   * @param mkPlayerConfiguration - configuration for this MKPlayer instance
   */
  constructor(containerElement: HTMLElement, mkPlayerConfiguration: MKPlayerConfiguration) {
    let playerConfig: PlayerConfig = {
      key: mkPlayerConfiguration.key,
      playback: {
        autoplay: mkPlayerConfiguration.autoplay,
        muted: mkPlayerConfiguration.muted,
      },
      buffer: {
        video: {
          forwardduration: 12,
          backwardduration: 12,
        },
        audio: {
          forwardduration: 12,
          backwardduration: 12,
        },
      },
      analytics: {
        key: '1c45ae9c-345e-4462-95e2-3e8faccd64f1',
      },
      ui: {
        disableAutoHideWhenHovered: true,
      },
      logs: {
        level: LogLevel.OFF,
        bitmovin: true,
      },
      network: {
        preprocessHttpRequest: (type: HttpRequestType, request: HttpRequest) => {
          if (type === 'manifest/hls/master' || type === 'manifest/hls/variant') {
            if (typeof mkPlayerConfiguration.refreshToken === 'function') {
              let urlType = this.stream.didFailOver ? UrlType.Secondary : UrlType.Primary;
              let token = mkPlayerConfiguration.refreshToken(urlType);
              console.log('[MKPlayer] Appending ' + urlType + ' token: ' + token);
              request.url = URLHelper.appendToken(request.url, token);
            }
          }

          return Promise.resolve(request);
        },
        preprocessHttpResponse: (type, response) => {
          if (type === 'manifest/hls/master') {
            let body = this.manipulateMasterPlaylist((response.body as string));
            // @ts-ignore
            response.body = body;
          }
          return Promise.resolve(response);
        },
      },
    };

    if (mkPlayerConfiguration.targetLatency > 0) {
      playerConfig.live = {
        lowLatency: {
          targetLatency: mkPlayerConfiguration.targetLatency,
          catchup: {
            playbackRateThreshold: 0.075,
            seekThreshold: 12,
            playbackRate: 1.2,
          },
          fallback: {
            playbackRateThreshold: 0.075,
            seekThreshold: 12,
            playbackRate: 0.8,
          },
        },
      };
    }

    let player = new Player(containerElement, playerConfig);
    if (mkPlayerConfiguration.ui !== false) {
      if (mkPlayerConfiguration.smallScreen) {
        UIFactory.buildDefaultSmallScreenUI(player);
      } else {
        UIFactory.buildDefaultUI(player, playerConfig.ui);
      }
    }

    if (mkPlayerConfiguration.debug) {
      Logger.enable();
    }

    this.player = player;
    console.log('%c MKPlayer ' + this.version() + ' www.mediakind.com', 'color: blue');
  }

  /**
   * start playback or resumes playback after the player has been paused
   */
  play(): Promise<void> {
    return this.player.play();
  }

  /**
   * pauses playback on the player
   */
  pause(): void {
    this.player.pause();
  }

  /**
   * destroys the video player. You must create a new MKPlayer if this method is called
   */
  destroy(): Promise<void> {
    return this.player.destroy();
  }

  /**
   * Loads a new stream for playback
   *
   * @param stream - the stream the player will load
   */
  load(stream: MKStream): Promise<void> {
    let playerSource: SourceConfig;
    this.stream = stream;
    let url: string = this.selectUrl();

    if (url.includes('.m3u8')) {
      playerSource = {
        hls: url,
      };
    } else if (url.includes('.mp4')) {
      playerSource = {
        progressive: url,
      };
    } else if (url.includes('.mpd')) {
      playerSource = {
        dash: url,
      };
    } else {
      playerSource = {
        smooth: url,
      };
    }

    if (stream.poster) {
      playerSource.poster = stream.poster;
    }

      playerSource.labeling = {
        hls: {
          tracks: (track) => {
            if (this.stream.labelConfig && this.stream.labelConfig.audio && this.stream.labelConfig.audio[track.lang]) {
              return this.stream.labelConfig.audio[track.lang];
            } else {
              return track.lang;
            }
          },
          subtitles: (track) => {
            console.log(track);
            if (this.stream.labelConfig && this.stream.labelConfig.captions && this.stream.labelConfig.captions[track.lang]) {
              return this.stream.labelConfig.captions[track.lang];
            } else {
              return track.lang;
            }
          },
        },
      };

    if (stream.assetIdentifier) {
      playerSource.analytics = {
        videoId: stream.assetIdentifier,
        userId: stream.userId,
        customData1: stream.customData1,
        customData2: stream.customData2,
        customData3: stream.customData3,
        customData4: stream.customData4,
        customData5: stream.customData5,
        customData6: this.version(),
      };
    }

    if (stream.subtitleTracks && stream.subtitleTracks[0] && stream.subtitleTracks[0].url && !stream.subtitleTracks[0].url.includes('.m3u8')) {
      playerSource.subtitleTracks = stream.subtitleTracks.map((track) => <SubtitleTrack>{
        lang: track.language,
        url: track.url,
        id: MKPlayer.makeId(4),
      });
    }

    Logger.log('[MKPlayer] loading source - ' + JSON.stringify(playerSource));

    return this.player.load(playerSource);
  }

  /**
   * Removes a handler for a MKEvent
   *
   * @param eventType
   * @param callback
   */
  off(eventType: MKEvent, callback: MKPlayerEventCallback): void {
    let playerEventType = MKPlayer.mapEvent(eventType);
    return this.player.off(playerEventType, callback);
  }

  /**
   * Subscribes and event handler to a MKEvent
   *
   * @param eventType
   * @param callback
   */
  on(eventType: MKEvent, callback: MKPlayerEventCallback): void {
    let playerEventType = MKPlayer.mapEvent(eventType);
    return this.player.on(playerEventType, callback);
  }

  /**
   * unloads the current playing stream
   */
  unload(): Promise<void> {
    return this.player.unload();
  }

  /**
   * gets the player's current time
   */
  getCurrentTime(): number {
    return this.player.getCurrentTime();
  }

  /**
   * gets the current audio and video buffer levels
   *
   * @returns
   */
  getBufferLevels(): BufferLevel {
    return {
      audio: this.player.buffer.getLevel(BufferType.ForwardDuration, MediaType.Audio).level,
      video: this.player.buffer.getLevel(BufferType.ForwardDuration, MediaType.Video).level,
    };
  }

  /**
   * sets the target latency during live playback
   * @param latency
   */
  setTargetLatency(latency: number) {
    this.player.lowlatency.setTargetLatency(latency);
  }

  /**
   * sets the target latency during live playback
   * @param latency
   */
  getLatency(): number {
    return this.player.lowlatency.getLatency();
  }

  /**
   * mutes the player
   */
  mute(): void {
    this.player.mute();
  }

  /**
   * gets the current playback speed
   */
  playbackSpeed(): number {
    return this.player.getPlaybackSpeed();
  }

  /**
   * unmute the player
   */
  unmute(): void {
    this.player.unmute();
  }

  /**
   * sets the player's volume in the range of 0 (silent) to 100 (max volume)
   *
   * @param volume The volume to set between 0 and 100
   */
  setVolume(volume: number): void {
    this.player.setVolume(volume);
  }

  /**
   * returns the current player version
   */
  version(): string {
    return '1.0.20';
  }

  private selectUrl(): string {
    let rand = MKPlayer.getRandomInt(99);
    if (this.stream.cdnFailoverPercent && this.stream.secondaryUrl && rand < this.stream.cdnFailoverPercent) {
      Logger.log('[MKPlayer] selecting secondary source url - ' + this.stream.secondaryUrl);
      this.stream.didFailOver = true;
      return this.stream.secondaryUrl;
    } else {
      Logger.log('[MKPlayer] selecting primary source url - ' + this.stream.primaryUrl);
      this.stream.didFailOver = false;
      return this.stream.primaryUrl;
    }
  }

  private static getRandomInt(max: number) {
    return Math.floor(Math.random() * Math.floor(max));
  }

  private static makeId(length) {
    let result = '';
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  private static mapEvent(event: MKEvent): PlayerEvent {
    let playerEventType: PlayerEvent;
    switch (event) {
      case MKEvent.BufferingStarted:
        playerEventType = PlayerEvent.StallStarted;
        break;
      case MKEvent.BufferingEnded:
        playerEventType = PlayerEvent.StallEnded;
        break;
      case MKEvent.Playing:
        playerEventType = PlayerEvent.Playing;
        break;
      case MKEvent.Paused:
        playerEventType = PlayerEvent.Paused;
        break;
      case MKEvent.TimeChanged:
        playerEventType = PlayerEvent.TimeChanged;
        break;
      case MKEvent.Ready:
        playerEventType = PlayerEvent.Ready;
        break;
      case MKEvent.Error:
        playerEventType = PlayerEvent.Error;
        break;
      case MKEvent.StreamFinished:
        playerEventType = PlayerEvent.PlaybackFinished;
        break;
      case MKEvent.Warning:
        playerEventType = PlayerEvent.Warning;
        break;
      case MKEvent.QualityChanged:
        playerEventType = PlayerEvent.VideoPlaybackQualityChanged;
        break;
      case MKEvent.SegmentPlayback:
        playerEventType = PlayerEvent.SegmentPlayback;
        break;
      case MKEvent.Unloaded:
        playerEventType = PlayerEvent.SourceUnloaded;
        break;
      case MKEvent.Metadata:
        playerEventType = PlayerEvent.Metadata;
        break;
      case MKEvent.MetadataParsed:
        playerEventType = PlayerEvent.MetadataParsed;
        break;
    }
    return playerEventType;
  }

  private manipulateMasterPlaylist(response: string): string {
    let body = (response as string);
    if (this.stream.subtitleTracks && this.stream.subtitleTracks[0] && this.stream.subtitleTracks[0].url && this.stream.subtitleTracks[0].url.includes('.m3u8')) {
      this.stream.subtitleTracks.forEach((track) => {
        body = body.concat('\n#EXT-X-MEDIA:TYPE=SUBTITLES,'
          + 'NAME="' + track.language + '",'
          + 'DEFAULT=YES,'
          + 'AUTOSELECT=YES,'
          + 'FORCED=NO,'
          + 'LANGUAGE="' + track.language + '",GROUP-ID="Subtitles",'
          + 'URI="' + track.url + '"');
      });
    }

    return body;
  }
}
